import { useEffect, useRef } from "react";

const categories = [
  {
    id: "engineering",
    title: "エンジニアリングサービス",
    lead: "通信インフラの設計・構築・運用を、専門知識を持つスタッフが一貫してサポートします。",
    icon: "ri-cpu-line",
    services: [
      {
        icon: "ri-map-pin-range-line",
        title: "エリア設計",
        desc: "屋外・屋内を問わず、電波の反射・回折特性をシミュレーションし、ユーザーが快適に利用できる最適な通信エリアを設計します。",
        points: [
          "エリアシミュレーションの実施および結果レポート作成",
          "通信品質・ログデータの解析と改善提案",
        ],
      },
      {
        icon: "ri-router-line",
        title: "ネットワーク構築",
        desc: "携帯基地局に設置された無線機やアンテナなど、各種設備に対してネットワーク構築に必要なパラメータを作成・設定します。",
        points: [
          "無線機・アンテナ等の設備パラメータ作成および更新",
          "基地局設備へのデータ投入作業",
        ],
      },
      {
        icon: "ri-database-2-line",
        title: "システム運用",
        desc: "通信キャリアのシステムを安定稼働させるためのデータベース管理や、総務省への免許申請書類の作成・提出までをサポートします。",
        points: [
          "基地局諸元データのデータベース管理",
          "基地局契約内容の確認・管理",
          "無線局免許状の作成から申請書類提出まで",
        ],
      },
      {
        icon: "ri-pencil-ruler-2-line",
        title: "図面設計・作図",
        desc: "基地局の新設・改修にともなう図面作成から竣工図・申請図書の作成まで、正確・迅速に対応します。",
        points: [
          "基地局の設計図面作成および作図",
          "竣工図・各種申請図書の作成",
        ],
      },
    ],
  },
  {
    id: "field",
    title: "フィールドサービス",
    lead: "経験豊富なエンジニアが現地に赴き、通信インフラの構築から保守まで現場をしっかり支えます。",
    icon: "ri-signal-tower-line",
    services: [
      {
        icon: "ri-radar-line",
        title: "電波測定調査",
        desc: "主要通信キャリアに対応した専用測定機器を用いて、屋外ドライブテストや屋内測定など様々な環境での電波状況を正確に計測・分析し、ネットワーク品質の最適化を支援します。",
        points: [
          "電界強度など各種データの現地取得",
          "測定データの分析・解析および報告書作成",
          "通信品質向上に向けた改善策の立案・提案",
        ],
      },
      {
        icon: "ri-archive-stack-line",
        title: "資材管理・調整",
        desc: "基地局の建設・設置現場において必要な資材の調達・在庫管理から、施工会社への作業指示・スケジュール調整まで一元管理します。",
        points: [
          "資機材の出荷・搬出・在庫管理",
          "管理システムを活用した監視・調整業務",
          "施工会社への作業指示およびスケジュール調整",
        ],
      },
      {
        icon: "ri-tools-fill",
        title: "保守",
        desc: "通信設備の定期点検・障害対応・予防保全を行い、設備の安定稼働を継続的に維持します。トラブル発生時には迅速に現場対応し、通信品質の低下を防ぎます。",
        points: [
          "通信設備の定期点検および異常検知",
          "障害発生時の迅速な現地対応・復旧作業",
          "設備の予防保全および劣化診断",
          "点検報告書の作成および提出",
        ],
      },
      {
        icon: "ri-file-list-3-line",
        title: "登録検査",
        desc: "電波法に基づき、無線局の定期検査・変更検査を専門スタッフが実施します。法令に沿った正確・迅速な対応で、安心して設備を運用できる環境をサポートします。",
        points: [
          "無線局の定期検査および変更検査の実施",
          "検査結果の記録・報告書作成",
          "法令に基づく検査スケジュールの管理・調整",
        ],
      },
      {
        icon: "ri-hammer-line",
        title: "施工管理",
        desc: "安全・品質・工程を管理しながら、基地局設備の施工を統括します。",
        points: [
          "候補地における地物・電波伝搬状況の確認",
          "無線アンテナ・無線機器の設置および通信ケーブルの敷設",
          "設計図面に基づく施工品質の確認・管理",
        ],
      },
    ],
  },
];

export default function Services() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".service-card").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0)";
              }, i * 80);
            });
            entry.target.querySelectorAll(".section-title-el").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
              }, i * 80);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      ref={ref}
      className="py-24 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #0A1F44 0%, #071530 50%, #0A1F44 100%)" }}
    >
      <div className="absolute top-0 left-0 right-0 h-px section-divider" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-inexx-blue/5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Title */}
        <div className="text-center mb-16">
          <p
            className="section-title-el text-inexx-blue text-sm font-semibold tracking-widest uppercase mb-3 transition-opacity duration-500"
            style={{ opacity: 0 }}
          >
            Our Services
          </p>
          <h2
            className="section-title-el text-3xl md:text-4xl font-bold text-white transition-opacity duration-500"
            style={{ opacity: 0 }}
          >
            業務内容
          </h2>
          <p
            className="section-title-el text-gray-400 mt-4 max-w-xl mx-auto text-sm md:text-base transition-opacity duration-500"
            style={{ opacity: 0 }}
          >
            移動体通信に関するあらゆるニーズに一貫して対応します。
          </p>
          <div
            className="section-title-el mx-auto mt-4 w-16 h-0.5 bg-inexx-blue transition-opacity duration-500"
            style={{ opacity: 0 }}
          />
        </div>

        {/* Categories */}
        <div className="flex flex-col gap-20">
          {categories.map((cat) => (
            <div key={cat.id}>
              {/* Category Header */}
              <div
                className="service-card flex flex-col gap-3 mb-8 pb-6 rounded-xl px-6 py-5"
                style={{
                  opacity: 0,
                  transform: "translateY(20px)",
                  transition: "opacity 0.5s ease, transform 0.5s ease",
                  background: "rgba(0,119,204,0.08)",
                  border: "1px solid rgba(0,119,204,0.25)",
                }}
              >
                {/* Row 1: Icon + Title */}
                <div className="flex flex-row items-center flex-nowrap gap-3">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl bg-inexx-blue/20 flex-shrink-0"
                    style={{ border: "1px solid rgba(0,119,204,0.4)" }}
                  >
                    <i className={`${cat.icon} text-2xl text-inexx-blue`} />
                  </div>
                  <h3 className="text-[18px] md:text-[24px] font-bold text-white whitespace-nowrap">{cat.title}</h3>
                </div>
                {/* Row 2: Lead text */}
                <p className="text-gray-400 text-[14px] md:text-[16px] leading-relaxed max-w-3xl">{cat.lead}</p>
              </div>

              {/* Service Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {cat.services.map((service) => (
                  <div
                    key={service.title}
                    className="service-card group cursor-pointer rounded-xl p-6 flex flex-col gap-4"
                    style={{
                      background: "rgba(13,41,82,0.6)",
                      border: "1px solid rgba(0,119,204,0.2)",
                      opacity: 0,
                      transform: "translateY(20px)",
                      transition: "opacity 0.5s ease, transform 0.5s ease, border-color 0.3s ease, background 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(0,119,204,0.55)";
                      (e.currentTarget as HTMLDivElement).style.background = "rgba(0,119,204,0.10)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(0,119,204,0.2)";
                      (e.currentTarget as HTMLDivElement).style.background = "rgba(13,41,82,0.6)";
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-300 group-hover:scale-110"
                        style={{ background: "rgba(0,119,204,0.15)", border: "1px solid rgba(0,119,204,0.3)" }}
                      >
                        <i className={`${service.icon} text-xl text-inexx-blue`} />
                      </div>
                      <h4 className="text-white font-bold text-[18px] md:text-[20px] leading-snug">{service.title}</h4>
                    </div>
                    <p className="text-gray-400 text-xs leading-relaxed">{service.desc}</p>
                    {service.points.length > 0 && (
                      <ul className="flex flex-col gap-1.5 mt-auto">
                        {service.points.map((pt) => (
                          <li key={pt} className="flex items-start gap-2 text-gray-500 text-xs">
                            <span className="text-inexx-blue mt-0.5 flex-shrink-0">・</span>
                            <span>{pt}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
